import { useEffect, useState, useCallback } from "react";

import { X, ZoomIn, ZoomOut, RotateCcw, RotateCw, RefreshCw } from "lucide-react";

import { cn } from "@/lib/utils";

interface ImageLightboxProps {
  /** Full-resolution image URL or `data:` URL. `null` keeps the lightbox closed. */
  src: string | null;
  /** Accessible alt text for the image. */
  alt?: string;
  /** Called when the user dismisses the lightbox (mask, Escape, or close button). */
  onClose: () => void;
  /** Optional extra classes for the overlay container. */
  className?: string;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const BUTTON_STEP = 0.5;
const WHEEL_STEP = 0.2;

const clampScale = (value: number): number =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

/**
 * Full-screen image preview modal — an Ant-Design-style lightbox: a dark mask,
 * a centered image with zoom + rotate controls in a floating toolbar, and a
 * close button. Implemented as a plain fixed overlay (not a shadcn Dialog) so it
 * can render `data:` URLs (e.g. base64 screenshots) that cannot open in a new
 * tab. Dismiss via mask click, the close button, or the Escape key. Renders
 * nothing when `src` is `null`. Reusable across any image-preview surface.
 */
export function ImageLightbox({
  src,
  alt = "Image preview",
  onClose,
  className,
}: ImageLightboxProps): JSX.Element | null {
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);

  // Reset the transform whenever a new image is opened.
  useEffect(() => {
    if (src) {
      setScale(1);
      setRotate(0);
    }
  }, [src]);

  useEffect(() => {
    if (!src) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [src, onClose]);

  const zoomIn = useCallback(
    (): void => setScale((s) => clampScale(s + BUTTON_STEP)),
    [],
  );
  const zoomOut = useCallback(
    (): void => setScale((s) => clampScale(s - BUTTON_STEP)),
    [],
  );
  const rotateLeft = useCallback(
    (): void => setRotate((r) => r - 90),
    [],
  );
  const rotateRight = useCallback(
    (): void => setRotate((r) => r + 90),
    [],
  );
  const reset = useCallback((): void => {
    setScale(1);
    setRotate(0);
  }, []);

  if (!src) return null;

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>): void => {
    const direction = event.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP;
    setScale((s) => clampScale(s + direction));
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm",
        className,
      )}
      onClick={onClose}
      onWheel={handleWheel}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      data-testid="image-lightbox"
    >
      {/* Close button — top-right. */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        aria-label="Close preview"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Image — stops propagation so interacting with it never closes the mask. */}
      <img
        src={src}
        alt={alt}
        onClick={(event) => event.stopPropagation()}
        style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
        className="max-h-[85vh] max-w-[90vw] select-none rounded object-contain shadow-2xl transition-transform duration-200"
        draggable={false}
      />

      {/* Toolbar — bottom-center floating pill. */}
      <div
        onClick={(event) => event.stopPropagation()}
        className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/60 px-2 py-1.5 text-white shadow-lg backdrop-blur"
      >
        <ToolbarButton
          label="Zoom out"
          onClick={zoomOut}
          disabled={scale <= MIN_SCALE}
        >
          <ZoomOut className="h-5 w-5" />
        </ToolbarButton>
        <ToolbarButton
          label="Zoom in"
          onClick={zoomIn}
          disabled={scale >= MAX_SCALE}
        >
          <ZoomIn className="h-5 w-5" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-white/20" aria-hidden="true" />
        <ToolbarButton label="Rotate left" onClick={rotateLeft}>
          <RotateCcw className="h-5 w-5" />
        </ToolbarButton>
        <ToolbarButton label="Rotate right" onClick={rotateRight}>
          <RotateCw className="h-5 w-5" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-white/20" aria-hidden="true" />
        <ToolbarButton label="Reset" onClick={reset}>
          <RefreshCw className="h-5 w-5" />
        </ToolbarButton>
      </div>
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

function ToolbarButton({
  label,
  onClick,
  disabled = false,
  children,
}: ToolbarButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
